import {
  Menu,
  MenuButton,
  MenuItemRadio,
  MenuProvider
} from '@ariakit/react/menu';
import { domAnimation, motion } from 'framer-motion';
import {
  type ChangeEvent,
  type ReactElement,
  type ReactNode,
  useState
} from 'react';

import ChevronIcon from '~/assets/icons/Chevron';
import CloseIcon from '~/assets/icons/Close';
import FramerMotion from '~/components/FramerMotion';

import DropdownPanel from './DropdownPanel';
import RadioMark from './RadioMark';

type Props = {
  chevronStroke?: string;
  children: (close: () => void) => ReactNode;
  className?: string;
  fitContent?: boolean;
  name: string;
  onOpen?: () => void;
  search?: {
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    value: string;
  };
  selectedValue?: string;
  trigger: ReactNode;
  triggerClassName: string;
};

export default function Dropdown({
  children,
  chevronStroke = 'currentColor',
  className,
  fitContent,
  name,
  onOpen,
  search,
  selectedValue,
  trigger,
  triggerClassName
}: Props) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <MenuProvider
      defaultValues={{ [name]: selectedValue ?? '' }}
      open={isOpen}
      setOpen={(open) => {
        if (open) onOpen?.();
        setIsOpen(open);
      }}
    >
      <MenuButton className={triggerClassName}>
        {trigger}
        <div className={isOpen ? 'rotate-180' : ''}>
          <ChevronIcon stroke={chevronStroke} width="10" height="10" />
        </div>
      </MenuButton>
      <FramerMotion feature={domAnimation}>
        <Menu
          render={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
          }
          gutter={8}
          className={className}
        >
          <DropdownPanel fitContent={fitContent} search={search}>
            {children(() => setIsOpen(false))}
          </DropdownPanel>
        </Menu>
      </FramerMotion>
    </MenuProvider>
  );
}

export function DropdownOption({
  canClear,
  children,
  isSelected,
  name,
  onClick,
  render,
  value
}: {
  canClear?: boolean;
  children: ReactNode;
  isSelected: boolean;
  name: string;
  onClick?: () => void;
  render?: ReactElement;
  value: string;
}) {
  return (
    <MenuItemRadio
      checked={isSelected}
      name={name}
      value={value}
      onClick={onClick}
      render={render}
      className="group flex w-full items-center gap-3 p-4 pr-6 text-sm"
    >
      {canClear && isSelected ? (
        <span className="flex size-4 shrink-0 items-center justify-center">
          <span className="group-hover:hidden">
            <RadioMark isSelected />
          </span>
          <span className="hidden group-hover:block">
            <CloseIcon height="10" stroke="black" width="10" />
          </span>
        </span>
      ) : (
        <RadioMark isSelected={isSelected} />
      )}
      {children}
    </MenuItemRadio>
  );
}
